import CoinSelectionDropdown from "features/portfolio/Assets/CoinSelectionDropdown";
import { useEffect, useMemo, useState } from "react";
import ExchangeSelectionDropdown from "features/portfolio/Assets/ExchangeSelectionDropdown";
import { sharedLib } from "@shared/services/shared.service.ts";
import { LanguageNameSpaces, PieChartDataPoint } from "@shared/types";
import TradingViewChart from "common/Charts/TradingViewChart.tsx";
import Title from "common/Typography/Title.tsx";
import Flex from "common/Boxes/Flex.tsx";
import useMedia from "utils/hooks/useMedia.ts";
import { MOBILE_LAYOUT_ONE_COLUMN } from "common/Layouts/TwoChartsTableLayout.tsx";
import styled, { useTheme } from "styled-components";
import { NewLookCardTitle } from "features/portfolio/NewLook/styles.ts";
import { skipToken } from "@reduxjs/toolkit/query";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface CoinsPricesChartProps {
  pieChartData: PieChartDataPoint[];
  defaultSymbol?: string;
  defaultExchange?: string;
  loading?: boolean;
  loaded?: boolean;
}

const CoinsPricesChart = ({
  pieChartData,
  defaultExchange,
  defaultSymbol,
  loading,
  loaded,
}: CoinsPricesChartProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const { isNewLook } = useTheme();
  const isSmallScreen = useMedia([`(max-width: 1300px)`], [true], false);
  const isOneColumnLayout = useMedia(
    [`(max-width: ${MOBILE_LAYOUT_ONE_COLUMN})`],
    [true],
    false
  );
  const [symbol, setSymbol] = useState<string>("");
  const [exchange, setExchange] = useState<string>("");

  const { data: symbols } = sharedLib.sharedApi.trades.useGetSymbolsQuery();

  const { data: exchanges, isFetching: getExchangesFetching } =
    sharedLib.sharedApi.portfolio.useGetExchangesFromSymbolQuery(
      symbol ? { symbol } : skipToken
    );

  useEffect(() => {
    if (defaultSymbol) setSymbol(defaultSymbol);
  }, [defaultSymbol]);

  useEffect(() => {
    if (defaultExchange) setExchange(defaultExchange);
  }, [defaultExchange]);

  const tradingViewChart = (
    <TradingViewChart
      symbol={symbol}
      exchange={exchange}
      isCard={!isOneColumnLayout}
      minimize={true}
      spinnerSize="default"
    />
  );

  const coinSelector = useMemo(
    () => (
      <CoinSelectionDropdown
        symbol={symbol}
        symbols={loading ? undefined : symbols?.active_symbols}
        setSymbol={setSymbol}
        pieChartData={pieChartData}
        checkBlacklist={true}
        loaded={loaded}
      />
    ),
    [symbol, symbols?.active_symbols, pieChartData, loading, loaded]
  );

  const exchangeSelector = useMemo(
    () => (
      <ExchangeSelectionDropdown
        exchange={exchange}
        exchanges={loading ? undefined : exchanges}
        setExchange={setExchange}
        loading={getExchangesFetching}
      />
    ),
    [exchange, exchanges, loading, getExchangesFetching]
  );

  const selectors = (
    <Selectors $isSmallScreen={isSmallScreen}>
      {coinSelector}
      {exchangeSelector}
    </Selectors>
  );

  if (isNewLook) {
    return (
      <>
        <NewLookHeader>
          <NewLookCardTitle>{t("charts.price_change")}</NewLookCardTitle>
          <NewLookSelectors>
            {coinSelector}
            {exchangeSelector}
          </NewLookSelectors>
        </NewLookHeader>
        {tradingViewChart}
      </>
    );
  }

  return (
    <>
      <Flex style={{ marginBottom: isSmallScreen ? 4 : 8 }}>
        <Title level={3} type="secondary">
          {t("charts.price_change")}
        </Title>
        {!isSmallScreen && selectors}
      </Flex>
      {isSmallScreen && selectors}
      {tradingViewChart}
    </>
  );
};

export default CoinsPricesChart;

const Selectors = styled(Flex)<{ $isSmallScreen?: boolean }>`
  justify-content: flex-end;
  ${(p) =>
    p.$isSmallScreen &&
    `
    margin-bottom: 8px;
  `};
`;
const NewLookHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin-bottom: 8px;
`;
const NewLookSelectors = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-right: -8px;
`;
